
import React, { useMemo } from 'react';
import type { MenuData, MenuItem } from '../types';
import { getImageForDish } from '../utils/images';
import { menuItems } from '../data/menuItems';

const MenuCategory: React.FC<{ title: string; items: MenuItem[] }> = ({ title, items }) => {
  // Static mapping to ensure correct images, especially for Chicken 65
  const getStaticImageForDish = (dishName: string): string => {
    const staticImageMap: { [key: string]: string } = {
      'Chicken 65': '/images/Chicken 65.png',
      'Beef Shawarma': '/images/Beef Shawarma.PNG',
      'Chicken Over Rice': '/images/Chicken Over Rice.JPG',
      'Chicken Tikka Tacos': '/images/Chicken Tikka Tacos.PNG',
      'Double Patty Burger': '/images/Double Patty Burger.PNG',
      'Gajar Ka Halwa': '/images/Gajar ka Halwa.PNG',
      'Hyderabadi Mutton Dum Biryani': '/images/Mutton Dum Biryani.PNG',
      'Hyderabadi Chicken Dum Biryani': '/images/Chicken Dum Biryani.PNG',
      'Hyderabadi Chicken Korma': '/images/Chicken Korma.png',
      'Hyderabadi Mutton Korma': '/images/Mutton Korma.png',
      'Hyderabadi Chicken 65 Burger': '/images/Chicken 65 Burger.png',
      'Hyderabadi Chilli Chicken': '/images/Chilli Chicken.png',
      'Hyderabadi Achaari Chicken': '/images/Achaari Chicken.png',
      'Hyderabadi Chicken Shawarma': '/images/Chicken Shawarma.png',
      'Hyderabadi Chicken Sliders': '/images/Chicken Sliders.png',
      'Hyderabadi Kaddu ki Kheer': '/images/Kaddu Ki Kheer.png',
      'Hyderabadi Double ka Meetha': '/images/Double Ka Meetha.png',
      'Hyderabadi Qubani ka Meetha': '/images/Hyderabadi Qubaani Ka Meetha.png',
      'Smash Burger': '/images/Smash Burger.PNG'
    };
    return staticImageMap[dishName] || '/images/Chicken 65.png'; // Default fallback
  };

  // Memoize images with static mapping
  const itemsWithImages = useMemo(() => {
    return items.map(item => ({
      ...item,
      imageUrl: getStaticImageForDish(item.name)
    }));
  }, [items]);

  return (
    <div className="mb-12">
      <h3 className="font-serif text-3xl font-bold text-center mb-8 text-[#f0e1b2] tracking-widest">{title}</h3>
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
        {itemsWithImages.map((item, index) => (
          <div key={index} className="group cursor-pointer border border-[#d4af37]/20 rounded-lg p-4 transition-all duration-300 ease-in-out hover:scale-105 hover:border-[#d4af37]/50 bg-black/20">
            <div className="flex gap-4">
              <img 
                src={item.imageUrl} 
                alt={item.name}
                className="w-20 h-20 object-cover rounded-lg shadow-md flex-shrink-0"
              />
              <div className="flex-grow">
                <h4 className="font-serif text-xl font-semibold text-white">{item.name}</h4>
                <p className="text-gray-400 italic pt-2 max-h-0 opacity-0 transition-all duration-500 ease-in-out group-hover:max-h-40 group-hover:opacity-100">
                  {item.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Menu: React.FC = () => {
  return (
    <section id="menu" className="py-20 bg-[#0c1a18]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl font-bold text-[#f0e1b2] mb-4">Our Culinary Creations</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            A selection of dishes crafted with passion, blending authentic Indian traditions with a modern flair.
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <MenuCategory title="Appetizers" items={menuItems.appetizers} />
          <MenuCategory title="Main Courses" items={menuItems.mains} />
          <MenuCategory title="Desserts" items={menuItems.desserts} />
        </div>
      </div>
    </section>
  );
};

export default Menu;